<template>
    <div class="content-top pr-1 sm:pr-6">
        <Head title="Upload Your Challenge Video" />

        <heading>
            <template #heading>
                <div class="flex items-center">
                    <Link :href="post.showUrl" class="-m-px p-2 hover:text-blue-500 focus:text-blue-600 hover:bg-white focus:bg-white rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                        </svg>
                    </Link>
                    <span class="ml-2">Upload Your Challenge Video</span>
                </div>
            </template>
        </heading>

        <div class="mb-10 lg:grid lg:gap-4 lg:grid-cols-12">
            <div class="flex flex-col space-y-4 lg:col-span-8 lg:px-0">
                <!-- Warning -->
                <div v-if="!post.isChallenger" class="mx-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg shadow lg:mx-0">
                    <div class="flex items-center">
                        <div class="flex-shrink-0">
                            <!-- Heroicon name: solid/exclamation -->
                            <svg aria-hidden="true" class="w-7 h-7 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path clip-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" fill-rule="evenodd" />
                            </svg>
                        </div>
                        <div class="ml-3">
                            <p class="text-yellow-700 text-xs lg:text-sm">
                                This challenge requires the
                                <span class="has-tooltip inline-block">
                                    <abbr class="hover:text-yellow-600 text-yellow-700 cursor-help">challenger</abbr>
                                    <tooltip>Person who creates the challenge</tooltip>
                                </span>
                                to approve your request. Take a selfie and upload it in the form below.
                            </p>
                        </div>
                    </div>
                </div>

                <div class="bg-white rounded-lg shadow">
                    <form @submit.prevent="sendRequest" dusk="accept-challenge-form">
                        <div class="flex flex-col p-4 space-y-8">
                            <div>
                                <div class="relative mb-2 lg:mb-3">
                                    <div aria-hidden="true" class="absolute inset-0 flex items-center">
                                        <div class="w-full border-t border-gray-300"></div>
                                    </div>
                                    <div class="relative flex justify-start">
                                        <span class="pr-3 text-gray-900 text-lg font-medium bg-white">1. Take Your Selfie</span>
                                    </div>
                                </div>

                                <!-- Photo File Input -->
                                <input dusk="image-upload-button" name="photo" ref="photo" class="hidden" type="file" accept="image/png,image/jpeg,image/webp" @change="updatePhotoPreview" />

                                <div class="flex items-center space-x-8">
                                    <!-- Default Avatar -->
                                    <button v-if="!photoPreview" class="group relative" type="button" @click.prevent="selectNewPhoto">
                                        <span class="inline-block w-24 h-24 bg-gray-50 border-2 border-white rounded-full overflow-hidden">
                                            <svg class="w-full h-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                                            </svg>
                                        </span>
                                        <span class="absolute z-10 bottom-3 right-0 inline-block text-white bg-blue-500 group-hover:bg-blue-700 rounded-full ring-2 ring-white">
                                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 4v16m8-8H4" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                            </svg>
                                        </span>
                                    </button>

                                    <!-- Photo Preview -->
                                    <div v-if="photoPreview" @click.prevent="selectNewPhoto" class="w-full lg:w-1/3">
                                        <span :style="'background-size: cover; background-repeat: no-repeat; background-position: center center; background-image: url(\'' + photoPreview + '\');'" class="block w-full h-60 rounded shadow"> </span>
                                    </div>

                                    <div class="flex hidden items-center lg:block">
                                        <jet-secondary-button type="button" @click.native.prevent="selectNewPhoto">
                                            <div class="flex items-center space-x-2">
                                                <svg v-if="!photoPreview" class="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                                    <path d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                                </svg>
                                                <svg v-if="photoPreview" xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span class="hidden md:block">Upload Your Selfie</span>
                                            </div>
                                        </jet-secondary-button>
                                    </div>
                                </div>

                                <jet-input-error :message="form.errors.photo" class="mt-2" />
                            </div>

                            <div class="text-gray-600 text-sm">
                                <div class="relative mb-2 lg:mb-3">
                                    <div aria-hidden="true" class="absolute inset-0 flex items-center">
                                        <div class="w-full border-t border-gray-300"></div>
                                    </div>
                                    <div class="relative flex justify-start">
                                        <span class="pr-3 text-gray-900 text-lg font-medium bg-white">2. Upload Challenge Video</span>
                                    </div>
                                </div>

                                <video-uploader :post="post" @uploaded="videoUploaded = true" @videoPreview="setVideoPreview" />

                                <div v-if="playlistUrl" class="mt-3">
                                    <button @click="showVideo = !showVideo" type="button" class="text-blue-600 hover:underline text-sm">
                                        <span v-if="showVideo">Hide Uploaded Video</span>
                                        <span v-if="!showVideo">Watch Uploaded Video</span>
                                    </button>
                                </div>

                                <div v-if="showVideo">
                                    <video-player v-if="playlistUrl" :options="videoPlayerOptions"></video-player>
                                </div>
                            </div>

                            <div class="text-gray-600 text-sm">
                                <div class="relative mb-2 lg:mb-3">
                                    <div aria-hidden="true" class="absolute inset-0 flex items-center">
                                        <div class="w-full border-t border-gray-300"></div>
                                    </div>
                                    <div class="relative flex justify-start">
                                        <span class="pr-3 text-gray-900 text-lg font-medium bg-white">3. Review Challenge Details</span>
                                    </div>
                                </div>
                                <div v-html="post.html"></div>

                                <!-- Agree to terms -->
                                <div class="relative flex items-start mt-2 p-4 bg-indigo-50 rounded-lg">
                                    <div class="flex items-center h-5">
                                        <input id="terms" v-model="form.terms" aria-describedby="terms-description" class="w-4 h-4 text-indigo-600 border-gray-400 rounded focus:ring-indigo-500" name="terms" type="checkbox" />
                                    </div>
                                    <div class="ml-3 text-sm">
                                        <label class="text-indigo-600 font-medium cursor-pointer" for="terms">I understand and agree to the above challenge details.</label>
                                        <jet-input-error :message="form.errors.terms" class="mt-2" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="flex items-center justify-end px-4 py-3 text-right bg-gray-50 sm:px-6 sm:rounded-b-lg">
                            <!--<Link :href="post.showUrl" class="flex items-center text-base text-gray-500 hover:text-blue-500">-->
                            <!--    <svg class="mr-1 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">-->
                            <!--        <path d="M15 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />-->
                            <!--    </svg>-->
                            <!--    <span>Back</span>-->
                            <!--</Link>-->

                            <jet-button :disabled="!photoPreview || !videoPreview || !videoUploaded || !form.terms" dusk="send-request-button" :processing="form.processing">
                                <span>Send Request</span>
                                <svg class="-mr-1 ml-3 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path d="M14 5l7 7m0 0l-7 7m7-7H3" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                </svg>
                            </jet-button>
                        </div>
                    </form>
                </div>
            </div>

            <!-- Aside -->
            <div class="hidden lg:block lg:col-span-4">
                <div class="sticky top-8 space-y-4">
                    <!--Quit the challenge-->
                    <challenge-request-button v-if="post.isCompeting" as="button" method="delete" :href="route('detachUserFromChallenge', { post: post })" class="disabled:bg-rose-200 bg-rose-500 hover:bg-rose-700 active:bg-rose-800 ring-rose-500"> Quit The Challenge </challenge-request-button>
                    <div class="px-4 py-6 bg-white rounded-lg shadow">
                        <div class="block space-y-4">
                            <div class="flex flex-col justify-center align-middle text-center">
                                <div class="flex justify-center mb-1 mr-1 text-pink-500 text-6xl font-bold">
                                    <span class="currency font-display">{{ post.pledgedAmount | abbr }}</span>
                                </div>
                                <div class="text-pink-300 text-xs font-semibold uppercase">value of tokens pledged</div>
                            </div>

                            <!-- Supporter Stats -->
                            <div class="grid gap-1 grid-cols-3 text-center">
                                <div class="col-span-1">
                                    {{ post.pledgesCount | abbr }}
                                    <div class="text-gray-500 text-xs uppercase">
                                        {{ "pledge" | pluralize(post.pledgesCount) }}
                                    </div>
                                </div>
                                <div class="col-span-1">
                                    {{ post.likesCount | abbr }}
                                    <div class="text-gray-500 text-xs uppercase">
                                        {{ "like" | pluralize(post.likesCount) }}
                                    </div>
                                </div>
                                <div class="col-span-1">
                                    {{ post.commentsCount | abbr }}
                                    <div class="text-gray-500 text-xs uppercase">
                                        {{ "comment" | pluralize(post.commentsCount) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Link :href="post.showUrl" class="group block bg-white rounded-lg shadow space-y-4">
                        <!-- Featured Image -->
                        <div class="aspect-w-10 aspect-h-7 block p-0 w-full h-48 shadow-inner group-hover:opacity-75 transition duration-100 ease-in-out sm:rounded-t-lg sm:overflow-hidden" v-html="post.featuredImageHtml"></div>

                        <!-- Post Details -->
                        <div class="pb-3 px-4">
                            <div class="line-clamp-3 group-hover:text-gray-500 text-gray-700 text-sm transition duration-100 ease-in-out" v-html="post.html"></div>

                            <button class="flex items-center pt-3 group-hover:text-blue-500 text-gray-500 text-sm transition duration-100 ease-in-out">
                                <svg class="-ml-1 mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M15 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                </svg>
                                <span class="font-medium">Go to challenge</span>
                            </button>
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout"
import AppHead from "@/Components/AppHead"
import AppLink from "@/Components/AppLink"
import pledges from "@/Pages/Post/Pledges"
import JetInputError from "@/Jetstream/InputError"
import JetSecondaryButton from "@/Jetstream/SecondaryButton"
import JetFormSection from "@/Jetstream/FormSection"
import JetButton from "@/Jetstream/Button"
import Heading from "@/Components/Heading"
import Tooltip from "@/Components/Tooltip"
import ChunkedUploader from "@/Components/VideoUploader"
import VideoUploader from "@/Components/VideoUploader"
import VideoPlayer from "@/Components/VideoPlayer"
import ChallengeRequestButton from "@/Pages/Post/ChallengeRequestButton.vue"

export default {
    components: { ChallengeRequestButton, VideoUploader, VideoPlayer, ChunkedUploader, AppLink, Tooltip, Heading, JetButton, JetFormSection, JetSecondaryButton, JetInputError, pledges, AppHead },
    layout: AppLayout,

    remember: {
        data: ["post", "form", "photoPreview", "videoPreview"],
        key() {
            return `Post:${this.post.id}`
        },
    },

    computed: {
        playlistUrl() {
            if (!this.competitor.video) return false
            if (!this.competitor.video.processed) return false
            return this.competitor.video.playlistUrl
        },

        videoPlayerOptions() {
            return { sources: [{ src: this.playlistUrl }], fluid: true, autoplay: true }
        },
    },

    data() {
        return {
            post: this.$page.props.post,
            competitor: this.$page.props.competitor,
            form: this.$inertia.form({
                photo: null,
                terms: false,
            }),
            videoUploaded: false,
            photoPreview: null,
            videoPreview: null,
            showVideo: false,
        }
    },

    methods: {
        sendRequest() {
            if (this.$refs.photo) {
                this.form.photo = this.$refs.photo.files[0]
            }
            this.form.post(this.route("posts.submitVideo.store", { post: this.post, competitor: this.competitor }), {
                preserveScroll: true,
                onSuccess: () => this.form.reset(),
            })
        },

        selectNewPhoto() {
            this.$refs.photo.click()
        },

        updatePhotoPreview() {
            const photoInput = this.$refs.photo
            if (photoInput.files && photoInput.files[0]) {
                const reader = new FileReader()

                reader.onload = (e) => {
                    this.photoPreview = e.target.result // Update photo preview
                }

                reader.readAsDataURL(photoInput.files[0])

                // Update the form data
                this.form.set("photo", photoInput.files[0]) // Using 'set' method if you're using Inertia form or similar reactive form object
            }
        },

        deletePhoto() {
            this.form.photo = null
            this.photoPreview = null
        },

        setVideoPreview(img) {
            this.videoPreview = img
        },
    },
}
</script>
